import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import bannerImg from '../images/outside/alma_suite_bb_outside_house.jpg';

const outsideCtx = require.context('../images/outside', false, /\.(png|jpe?g|svg)$/);
const outsideImages = outsideCtx.keys().map(k => outsideCtx(k));

function DoveSiamoPage() {
    const { isEnLang } = useLanguage();
    const { ref: introRef, inView: introInView } = useInView({ triggerOnce: true, threshold: 0.1 });
    const { ref: poiRef,   inView: poiInView   } = useInView({ triggerOnce: true, threshold: 0.1 });

    useEffect(() => {
        document.title = isEnLang
            ? 'Location – B&B Alma Suite | Between Anzio and Nettuno, Lazio Coast'
            : 'Dove Siamo – B&B Alma Suite | Tra Anzio e Nettuno, Costa Laziale';
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', isEnLang
            ? 'B&B Alma Suite is located at Via Albenga 3/5, Nettuno (RM), between Anzio and Nettuno. A few km from the sea, with reserved parking.'
            : 'Il B&B Alma Suite si trova in Via Albenga 3/5, Nettuno (RM), tra Anzio e Nettuno. A pochi km dal mare, con parcheggio riservato.'
        );
    }, [isEnLang]);

    const T = isEnLang ? {
        label: 'Location',
        bannerTitle: 'Where We Are',
        bannerSub: 'Between Anzio and Nettuno, on the Lazio coast',
        introLabel: 'Strategic Location',
        introTitle: 'The Perfect Starting Point for the Lazio Coast',
        introDesc: 'B&B Alma Suite is located at Via Albenga 3/5, Nettuno (RM). The property sits right on the border between Nettuno and Anzio — giving you easy access to the beaches, restaurants and attractions of both towns. The sea is just a few kilometres away, reachable in under 10 minutes by car.',
        poiTitle: 'What\'s Nearby',
        poiSub: 'Everything you need, a few minutes away',
        points: [
            { icon: '🏖️', title: 'Beaches of Anzio', desc: 'Less than 10 minutes by car. Sandy beaches and clear sea.' },
            { icon: '🏛️', title: 'Anzio Historic Centre', desc: 'Roman ruins, the port, seafront promenade and restaurants.' },
            { icon: '⚓', title: 'Nettuno Port', desc: 'Fishing harbour, fresh fish market and historic centre.' },
            { icon: '🌲', title: 'Circeo National Park', desc: 'About 30 km — nature trails, dunes and wildlife.' },
            { icon: '🚂', title: 'Padiglione Station', desc: 'The nearest train station is Padiglione, just a few minutes away. Direct line to Rome.' },
            { icon: '🛒', title: 'Supermarkets & Shops', desc: 'All essential services within walking or short driving distance.' },
        ],
        galleryTitle: 'The Property',
        gallerySub: 'Exterior and common areas',
        mapTitle: 'Find Us',
        ctaTitle: 'Book Your Stay',
        ctaSub: 'Via Albenga 3/5, Nettuno (RM) – contact us for rates and availability',
        ctaBook: 'Book via WhatsApp',
        ctaEmail: 'Send an Email',
    } : {
        label: 'Posizione',
        bannerTitle: 'Dove Siamo',
        bannerSub: 'Tra Anzio e Nettuno, sulla costa laziale',
        introLabel: 'Posizione Strategica',
        introTitle: 'Il Punto di Partenza Perfetto per la Costa Laziale',
        introDesc: "Il B&B Alma Suite si trova in Via Albenga 3/5, Nettuno (RM). La struttura è posizionata al confine tra Nettuno e Anzio — permettendoti di raggiungere facilmente le spiagge, i ristoranti e le attrazioni di entrambe le città. Il mare dista pochi chilometri, raggiungibile in meno di 10 minuti in auto.",
        poiTitle: 'Cosa C\'è Nei Dintorni',
        poiSub: 'Tutto quello che ti serve, a pochi minuti',
        points: [
            { icon: '🏖️', title: 'Spiagge di Anzio', desc: 'Meno di 10 minuti in auto. Spiagge sabbiose e mare cristallino.' },
            { icon: '🏛️', title: 'Centro Storico di Anzio', desc: 'Rovine romane, il porto, lungomare e ristoranti.' },
            { icon: '⚓', title: 'Porto di Nettuno', desc: 'Porto peschereccio, mercato del pesce fresco e centro storico.' },
            { icon: '🌲', title: 'Parco Nazionale del Circeo', desc: 'Circa 30 km — sentieri naturali, dune e fauna selvatica.' },
            { icon: '🚂', title: 'Stazione di Padiglione', desc: 'La stazione più vicina è Padiglione, a pochi minuti. Linea diretta per Roma.' },
            { icon: '🛒', title: 'Supermercati e Negozi', desc: 'Tutti i servizi essenziali a pochi passi o in auto.' },
        ],
        galleryTitle: 'La Struttura',
        gallerySub: 'Esterni e aree comuni',
        mapTitle: 'Come Raggiungerci',
        ctaTitle: 'Prenota il Tuo Soggiorno',
        ctaSub: 'Via Albenga 3/5, Nettuno (RM) – contattaci per tariffe e disponibilità',
        ctaBook: 'Prenota su WhatsApp',
        ctaEmail: 'Scrivici via Email',
    };

    return (
        <div className="inner-page">

            <div className="inner-banner" style={{ backgroundImage: `url(${bannerImg})` }}>
                <div className="inner-banner-overlay">
                    <div className="inner-banner-text">
                        <span className="inner-banner-label">{T.label}</span>
                        <h1>{T.bannerTitle}</h1>
                        <p>{T.bannerSub}</p>
                    </div>
                </div>
            </div>

            <section className="inner-intro">
                <div className="l-768">
                    <motion.div
                        ref={introRef}
                        initial={{ opacity: 0, y: 40 }}
                        animate={introInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8 }}
                    >
                        <span className="section-label">{T.introLabel}</span>
                        <h2>{T.introTitle}</h2>
                        <p>{T.introDesc}</p>
                    </motion.div>
                </div>
            </section>

            <section className="inner-features" style={{ background: 'var(--color-bg-alt)' }}>
                <motion.div
                    ref={poiRef}
                    className="inner-features-inner l-1260"
                    initial={{ opacity: 0, y: 40 }}
                    animate={poiInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                >
                    <h2>{T.poiTitle}</h2>
                    <p className="inner-features-sub">{T.poiSub}</p>
                    <div className="inner-features-grid">
                        {T.points.map((p, i) => (
                            <div className="inner-feature-item" key={i}>
                                <span className="inner-feature-icon">{p.icon}</span>
                                <h3>{p.title}</h3>
                                <p>{p.desc}</p>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </section>

            <section className="inner-gallery">
                <div className="inner-gallery-title l-1260">
                    <h2>{T.galleryTitle}</h2>
                    <p>{T.gallerySub}</p>
                </div>
                <div className="inner-gallery-grid l-1260">
                    {outsideImages.slice(0, 6).map((src, i) => (
                        <img
                            key={i}
                            src={src}
                            alt={isEnLang
                                ? `B&B Alma Suite exterior Anzio ${i + 1}`
                                : `Esterno B&B Alma Suite Anzio ${i + 1}`}
                            loading="lazy"
                        />
                    ))}
                </div>
            </section>

            <section className="inner-map-section">
                <div className="inner-map-title l-1260">
                    <h2>{T.mapTitle}</h2>
                </div>
                <iframe
                    title="Mappa B&B Alma Suite"
                    className="contact-map"
                    src="https://maps.google.com/maps?q=Via+Albenga+3%2F5%2C+Nettuno+RM&output=embed"
                    allowFullScreen
                    loading="lazy"
                />
            </section>

            <section className="inner-cta">
                <h2>{T.ctaTitle}</h2>
                <p>{T.ctaSub}</p>
                <div className="suite-cta-buttons">
                    <a
                        href={`https://wa.me/+393928729907?text=${encodeURIComponent("Salve, vorrei prenotare al B&B Alma Suite. www.almasuitebb.it")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary"
                    >{T.ctaBook}</a>
                    <a href="mailto:almasuitebb@gmail.com" className="btn-outline-white">{T.ctaEmail}</a>
                </div>
            </section>
        </div>
    );
}

export default DoveSiamoPage;
